import { memo, ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  loading?: boolean;
  error?: string | null;
  showCloseButton?: boolean;
  className?: string;
  contentClassName?: string;
  preventOutsideClick?: boolean;
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[90vw]',
};

export const BaseModal = memo(({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  loading = false,
  error = null,
  showCloseButton = true,
  className,
  contentClassName,
  preventOutsideClick = false,
}: BaseModalProps) => {
  const handleOpenChange = (open: boolean) => {
    if (!open && !loading && !preventOutsideClick) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent 
        className={cn(
          sizeClasses[size],
          'max-h-[90vh] overflow-hidden flex flex-col',
          contentClassName
        )}
        onPointerDownOutside={(e) => {
          if (preventOutsideClick || loading) {
            e.preventDefault();
          }
        }}
      >
        {(title || description) && (
          <DialogHeader>
            {title && <DialogTitle>{title}</DialogTitle>}
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
        )}
        
        <div className={cn('flex-1 overflow-y-auto', className)}>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : (
            children
          )}
        </div>
        
        {footer && (
          <DialogFooter>
            {footer}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
});

BaseModal.displayName = 'BaseModal';

// Common modal footer patterns
export const ModalFooter = memo(({
  onCancel,
  onConfirm,
  cancelText = 'Cancel',
  confirmText = 'Save',
  loading = false,
  disabled = false,
  variant = 'default' as 'default' | 'destructive',
}: {
  onCancel: () => void;
  onConfirm: () => void;
  cancelText?: string;
  confirmText?: string;
  loading?: boolean;
  disabled?: boolean;
  variant?: 'default' | 'destructive';
}) => (
  <>
    <Button
      variant="outline"
      onClick={onCancel}
      disabled={loading}
    >
      {cancelText}
    </Button>
    <Button
      variant={variant}
      onClick={onConfirm}
      disabled={disabled || loading}
    >
      {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
      {confirmText}
    </Button>
  </>
));

ModalFooter.displayName = 'ModalFooter';